import { useNavigate } from 'react-router-dom';
import { routes } from '@lib/routes';

export default function Landing() {
  const navigate = useNavigate();
  const salesEmailHref = 'mailto:francisco@isveritas.ai';

  return (
    <div className="min-h-screen bg-veritas-gray-50 font-sans text-veritas-navy">
      {/* 1. Top bar – logo left, nav right */}
      <div className="h-12 bg-veritas-navy flex items-center justify-between px-4 sm:px-6">
        <a href="/" className="flex items-center justify-center shrink-0" aria-label="Home">
          <img src="/brand/veritas-inverse.svg" alt="Veritas" className="h-8 w-auto" />
        </a>
        <nav className="flex items-center gap-4 sm:gap-6">
          <a href="#upload" onClick={(e) => { e.preventDefault(); navigate(routes.app); }} className="text-white text-xs uppercase tracking-wider hover:opacity-80 transition-opacity">Upload</a>
          <a href="#how-it-works" className="text-white text-xs uppercase tracking-wider hover:opacity-80 transition-opacity">How It Works</a>
          <a href="#pricing" className="text-white text-xs uppercase tracking-wider hover:opacity-80 transition-opacity">Pricing</a>
          <a href="#faq" className="text-white text-xs uppercase tracking-wider hover:opacity-80 transition-opacity">FAQ</a>
          <a href="#contact" onClick={(e) => { e.preventDefault(); navigate(routes.contact); }} className="text-white text-xs uppercase tracking-wider hover:opacity-80 transition-opacity">Contact</a>
        </nav>
      </div>

      {/* 2. Hero */}
      <section className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 max-w-6xl mx-auto">
        <h1 className="veritas-heading-hero veritas-hero-tagline text-left max-w-[80%] mb-6">
          <span className="block">Sober. Precise. Premium.</span>
          <span className="block">Forensic verification for digital media.</span>
        </h1>
        <p className="veritas-body leading-relaxed max-w-2xl mb-8">
          For audio, image, and video that may have been manipulated or AI-generated, Veritas produces evidence-based technical reports admissible in legal and editorial workflows.
        </p>
        <div className="flex flex-wrap gap-4 mb-10">
          <button type="button" onClick={() => navigate(routes.app)} className="bg-veritas-navy text-white font-sans font-semibold py-3 px-6 rounded-lg hover:bg-veritas-navy/90 transition-all duration-200">
            Upload a File
          </button>
          <a href="#how-it-works" className="inline-flex items-center px-6 py-3 bg-white text-veritas-navy font-sans font-semibold rounded-lg border border-veritas-navy/20 hover:bg-veritas-gray-50 transition-colors">
            How It Works
          </a>
        </div>
        <p className="veritas-body-light text-sm">Trusted by legal teams, journalists, and investigators.</p>
      </section>

      {/* 3. Detection Engine */}
      <section id="why" className="bg-white px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <p className="font-sans text-xl md:text-2xl text-veritas-navy font-bold leading-relaxed">
              Veritas streamlines verification from initial scan to forensic reporting, helping teams spend less time doubting reality and more time reporting the truth.
            </p>
            <p className="veritas-body text-base leading-relaxed self-start pt-1">
              Our multi-modal detection engine uses neural networks to identify manipulation traces invisible to the human eye, providing a confidence score you can trust.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1: Forensic Scanning */}
            <div className="flex flex-col">
              <div className="rounded-2xl bg-veritas-gray-50 p-6 mb-5 h-52 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-veritas-soft px-5 py-4 w-full max-w-[220px]">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-500 shrink-0" />
                    <span className="font-sans text-sm font-semibold text-veritas-navy">High Probability AI</span>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="font-sans text-xs text-veritas-gray-500">GAN Artifacts</span>
                        <span className="font-sans text-xs font-semibold text-veritas-navy">98.2%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-veritas-gray-200 overflow-hidden">
                        <div className="h-full rounded-full bg-red-500" style={{ width: '98.2%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="font-sans text-xs text-veritas-gray-500">Frequency Analysis</span>
                        <span className="font-sans text-xs font-semibold text-veritas-navy">84.5%</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-veritas-gray-200 overflow-hidden">
                        <div className="h-full rounded-full bg-amber-400" style={{ width: '84.5%' }} />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <h3 className="font-sans font-semibold text-veritas-navy text-lg mb-2">Forensic Scanning</h3>
              <p className="veritas-body text-sm leading-relaxed">
                Detect invisible artifacts left by generative adversarial networks (GANs) and diffusion models.
              </p>
            </div>
            {/* Card 2: Secure Chain of Custody */}
            <div className="flex flex-col">
              <div className="rounded-2xl bg-veritas-gray-50 p-6 mb-5 h-52 flex items-center justify-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/80 shadow-veritas-soft flex items-center justify-center">
                  <svg className="w-7 h-7 text-veritas-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" /></svg>
                </div>
                <div className="w-16 h-16 rounded-2xl bg-veritas-navy shadow-veritas-soft flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" /></svg>
                </div>
              </div>
              <h3 className="font-sans font-semibold text-veritas-navy text-lg mb-2">Secure Chain of Custody</h3>
              <p className="veritas-body text-sm leading-relaxed">
                Cryptographically sign verified media to ensure authenticity is maintained across platforms.
              </p>
            </div>
            {/* Card 3: Workflow Integration */}
            <div className="flex flex-col">
              <div className="rounded-2xl bg-veritas-gray-50 p-6 mb-5 h-52 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-veritas-soft px-5 py-4 w-full max-w-[220px] space-y-3">
                  {[
                    { icon: 'M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15', label: 'View Metadata' },
                    { icon: 'M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5', label: 'Deep Scan' },
                    { icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z', label: 'Generate Certificate' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3">
                      <svg className="w-4 h-4 text-veritas-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d={item.icon} />
                      </svg>
                      <span className="font-sans text-sm text-veritas-navy">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <h3 className="font-sans font-semibold text-veritas-navy text-lg mb-2">Workflow Integration</h3>
              <p className="veritas-body text-sm leading-relaxed">
                Seamlessly integrate verification into your editorial or moderation workflow via API.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. How It Works */}
      <section id="how-it-works" className="bg-veritas-navy px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="veritas-heading-dark text-3xl md:text-4xl lg:text-5xl mb-12 md:mb-16">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8 md:gap-12">
            {[
              {
                n: "1",
                title: "Upload Your File",
                body: "Accepted formats: audio, video, and image files up to 2 GB. Submit your file securely and we begin the process immediately.",
              },
              {
                n: "2",
                title: "We Analyze",
                body: "Our system triangulates technical signals: metadata and encoding traces, audio waveform and spectrogram, video frame analysis, and image compression patterns.",
              },
              {
                n: "3",
                title: "You Get a Report in 72 Hours",
                body: "Delivered encrypted via email: a Technical Certificate (PDF), Annex Pack with metadata and visuals, and JSON technical outputs. Faster turnaround available on request.",
              },
            ].map((step) => (
              <div key={step.n} className="flex flex-col">
                <div className="mb-6 flex justify-start">
                  <div className="w-12 h-12 rounded-full border border-white/30 flex items-center justify-center text-white/60 font-sans text-sm">
                    {step.n}
                  </div>
                </div>
                <h3 className="font-sans font-semibold text-white text-lg md:text-xl mb-4">{step.title}</h3>
                <p className="text-white/70 font-sans text-base leading-relaxed">{step.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. What You Get */}
      <section className="bg-white px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="veritas-heading-section mb-14">What You Get</h2>
          <div className="grid md:grid-cols-3 gap-8 items-start">

            {/* Card 1: Technical Certificate */}
            <div className="flex flex-col">
              <div className="rounded-2xl bg-veritas-gray-50 p-8 mb-6 h-64 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-veritas-soft p-5 w-full max-w-[240px]">
                  <div className="flex items-center gap-3 mb-4 pb-4 border-b border-veritas-gray-200">
                    <svg className="w-5 h-5 text-veritas-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15" /></svg>
                    <span className="font-sans font-semibold text-veritas-navy text-sm leading-tight">Technical<br />Certificate.pdf</span>
                  </div>
                  <p className="font-sans text-xs font-semibold text-veritas-gray-400 tracking-widest uppercase mb-3">Findings &amp; Limitations</p>
                  <div className="space-y-2">
                    <div className="h-2 rounded-full bg-veritas-gray-200 w-full" />
                    <div className="h-2 rounded-full bg-veritas-gray-200 w-4/5" />
                    <div className="h-2 rounded-full bg-veritas-gray-200 w-3/5" />
                  </div>
                </div>
              </div>
              <h3 className="font-sans text-2xl font-bold text-veritas-navy mb-3">Technical Certificate</h3>
              <p className="veritas-body text-sm leading-relaxed">
                A clear, structured document with findings and limitations, suitable for internal review or case preparation.
              </p>
            </div>

            {/* Card 2: Output Options */}
            <div className="flex flex-col">
              <div className="rounded-2xl bg-veritas-gray-50 p-8 mb-6 h-64 flex items-center justify-center">
                <div className="bg-white rounded-xl shadow-veritas-soft p-5 w-full max-w-[240px]">
                  <p className="font-sans font-semibold text-veritas-navy text-sm mb-4">Annex Pack PDF</p>
                  <ul className="space-y-3">
                    {[
                      'Verified hashes (SHA-256)',
                      'Metadata summaries',
                      'Visual aids (waveforms, spectrograms)',
                      'Key frames, ELA maps',
                    ].map((item) => (
                      <li key={item} className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-veritas-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" /></svg>
                        <span className="font-sans text-xs text-veritas-gray-500 leading-snug">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <h3 className="font-sans text-2xl font-bold text-veritas-navy mb-3">Output Options</h3>
              <p className="veritas-body text-sm leading-relaxed">
                Structured JSON for your own workflows or downstream analysis via API integration.
              </p>
            </div>

            {/* Card 3: Technical Output Files */}
            <div className="flex flex-col">
              <div className="rounded-2xl bg-veritas-navy p-8 mb-6 h-64 flex items-center justify-center">
                <div className="bg-white/10 rounded-xl p-5 w-full max-w-[240px] space-y-3">
                  {[
                    { label: 'JSON' },
                    { label: 'API' },
                    { label: 'Workflows' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-3 bg-white/10 rounded-lg px-4 py-2.5">
                      <span className="text-green-400 font-sans font-bold text-sm">✓</span>
                      <span className="font-sans text-white text-sm font-medium">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <h3 className="font-sans text-2xl font-bold text-veritas-navy mb-3">Technical Output Files</h3>
              <p className="veritas-body text-sm leading-relaxed">
                Optional upgrades available for expert testimony or extended workups.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* 6. Use Cases – image cards */}
      <section className="bg-veritas-navy">
        <div className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 max-w-6xl mx-auto">
          <h2 className="font-sans text-3xl md:text-4xl font-bold text-white mb-10">Veritas meets you where you work.</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            {
              title: 'Legal Teams',
              desc: 'Evidence you can present to counsel and courts.',
              img: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=600&h=800&fit=crop&auto=format',
            },
            {
              title: 'Newsrooms',
              desc: 'Validate content before publication.',
              img: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=600&h=800&fit=crop&auto=format',
              highlight: true,
            },
            {
              title: 'Platforms',
              desc: 'Scale moderation with automated forensic signals.',
              img: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=800&fit=crop&auto=format',
            },
            {
              title: 'Enterprise',
              desc: 'Dedicated workflows and volume pricing.',
              img: 'https://images.unsplash.com/photo-1486325212027-8081e485255e?w=600&h=800&fit=crop&auto=format',
            },
          ].map((item) => (
            <div
              key={item.title}
              className={`relative rounded-2xl overflow-hidden h-[420px] group cursor-pointer ${item.highlight ? 'ring-2 ring-indigo-400' : ''}`}
            >
              <img
                src={item.img}
                alt={item.title}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4">
                <span className="inline-block bg-white/95 backdrop-blur-sm text-veritas-navy font-sans font-medium text-sm px-3 py-1.5 rounded-lg shadow-veritas-soft">
                  {item.title}
                </span>
              </div>
            </div>
          ))}
          </div>
        </div>
      </section>

      {/* 8. Pricing */}
      <section id="pricing" className="px-4 sm:px-6 lg:px-8 py-16 md:py-24 bg-veritas-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="veritas-heading-section mb-2">Pricing</h2>
          <p className="veritas-body mb-12">Simple, transparent options. No subscriptions, pay per report.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {/* Tier 1: Triage */}
            <div className="bg-white rounded-2xl border border-veritas-navy/8 p-6 flex flex-col shadow-veritas-soft">
              <div className="mb-5">
                <h3 className="font-sans font-semibold text-veritas-navy text-xl mb-1">Triage</h3>
                <p className="font-sans text-sm text-veritas-gray-500">Automated forensic analysis for fast answers</p>
              </div>
              <div className="mb-6">
                <span className="font-sans text-4xl font-bold text-veritas-navy">€499</span>
                <span className="font-sans text-sm text-veritas-gray-500 ml-1">per file</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {[
                  'Technical Certificate (PDF)',
                  'SHA-256 hash verification',
                  'Metadata summary',
                  'Visual annex (waveforms, key frames)',
                  '72-hour turnaround',
                  'JSON technical outputs',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 font-sans text-sm text-veritas-gray-500">
                    <svg className="w-4 h-4 text-veritas-navy shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => navigate(routes.app)} className="w-full py-3 rounded-xl border border-veritas-navy/20 font-sans font-semibold text-sm text-veritas-navy hover:bg-veritas-gray-50 transition-colors">
                Get Started
              </button>
            </div>

            {/* Tier 2: Expert */}
            <div className="bg-veritas-navy rounded-2xl p-6 flex flex-col shadow-veritas-soft-lg relative">
              <div className="absolute top-4 right-4">
                <span className="bg-white/15 text-white font-sans text-xs font-medium px-2 py-1 rounded-full">Most Popular</span>
              </div>
              <div className="mb-5">
                <h3 className="font-sans font-semibold text-white text-xl mb-1">Expert Review</h3>
                <p className="font-sans text-sm text-white/60">Human-verified analysis with written assessment</p>
              </div>
              <div className="mb-6">
                <span className="font-sans text-4xl font-bold text-white">€999</span>
                <span className="font-sans text-sm text-white/60 ml-1">per file</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {[
                  'Everything in Triage',
                  'Expert analyst review',
                  'Written findings and limitations',
                  'Suitable for internal case prep',
                  'Priority turnaround available',
                  'Secure encrypted delivery',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 font-sans text-sm text-white/80">
                    <svg className="w-4 h-4 text-white shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <button type="button" onClick={() => navigate(routes.app)} className="w-full py-3 rounded-xl bg-white font-sans font-semibold text-sm text-veritas-navy hover:bg-veritas-gray-50 transition-colors">
                Get Started
              </button>
            </div>

            {/* Tier 3: Enterprise */}
            <div className="bg-white rounded-2xl border border-veritas-navy/8 p-6 flex flex-col shadow-veritas-soft">
              <div className="mb-5">
                <h3 className="font-sans font-semibold text-veritas-navy text-xl mb-1">Enterprise</h3>
                <p className="font-sans text-sm text-veritas-gray-500">Volume pricing for teams and agencies</p>
              </div>
              <div className="mb-6">
                <span className="font-sans text-4xl font-bold text-veritas-navy">Custom</span>
              </div>
              <ul className="space-y-3 mb-6 flex-1">
                {[
                  'Everything in Expert Review',
                  'Volume and bulk pricing',
                  'Dedicated analyst team',
                  'Custom turnaround SLAs',
                  'API and workflow integration',
                  'Expert witness support available',
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 font-sans text-sm text-veritas-gray-500">
                    <svg className="w-4 h-4 text-veritas-navy shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    {f}
                  </li>
                ))}
              </ul>
              <a href={salesEmailHref} className="flex w-full items-center justify-center py-3 rounded-xl bg-veritas-navy font-sans font-semibold text-sm text-white hover:bg-veritas-navy/90 transition-colors">
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ */}
      <section id="faq" className="bg-white px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="max-w-3xl mx-auto">
          <h2 className="veritas-heading-section mb-14">FAQ</h2>
          <dl>
            {[
              {
                q: "Is this a \"deepfake detector\"?",
                a: 'No single tool can claim 100% certainty. We provide forensic technical analysis, not binary judgement. Our goal is to give you evidence and context.',
              },
              {
                q: 'Can this be used in court?',
                a: 'Our default certificate is informational. For legal proceedings, we offer upgrades with expert witness support.',
              },
              {
                q: 'What formats are supported?',
                a: 'Common audio/video/image formats including MP4, MOV, WAV, MP3, JPG, PNG, WEBP, etc.',
              },
              {
                q: 'What if the file is compressed?',
                a: 'We document indicators of compression and explain their effects in the report.',
              },
            ].map((item, i, arr) => (
              <div key={item.q} className={`py-10 ${i < arr.length - 1 ? 'border-b border-veritas-navy/10' : ''}`}>
                <dt className="font-sans text-xl md:text-2xl font-bold text-veritas-navy mb-4">{item.q}</dt>
                <dd className="font-sans text-base text-veritas-gray-500 leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-veritas-navy px-4 sm:px-6 lg:px-8 pt-16 md:pt-24 pb-24 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 p-0 m-0 leading-none pointer-events-none">
          <img src="/brand/veritas-inverse.svg" alt="" aria-hidden="true" className="h-24 md:h-28 lg:h-32 w-auto opacity-95" />
        </div>
        <div
          className="absolute top-0 right-0 w-64 h-64 md:w-96 md:h-96 opacity-10"
          style={{ background: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.15) 0%, transparent 60%)' }}
        />
        <div className="max-w-6xl mx-auto relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10 mb-12">
            <div>
              <h2 className="font-sans text-2xl md:text-3xl text-white mb-4">Verify with forensic confidence.</h2>
              <p className="text-white/80 font-sans text-sm max-w-md mb-6">
                Get evidence-based technical reports for audio, image, and video. Fast and professionally documented.
              </p>
              <div className="flex flex-wrap gap-4">
                <button type="button" onClick={() => navigate(routes.app)} className="btn-veritas-white">
                  Upload a File
                </button>
                <a href={salesEmailHref} className="inline-flex items-center px-6 py-3 border border-white/30 text-white font-sans font-semibold rounded-lg hover:bg-white/10 transition-colors">
                  Contact Sales
                </a>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:justify-between gap-8 pt-10 border-t border-white/10">
            <p className="text-white/60 text-sm">© 2026 Veritas.</p>
            <nav className="grid grid-cols-2 sm:grid-cols-4 gap-8">
              {[
                { title: 'PRODUCT', links: ['Upload', 'How It Works', 'Pricing'] },
                { title: 'COMPANY', links: ['About', 'Contact'] },
                { title: 'RESOURCES', links: ['FAQ', 'Formats'] },
                { title: 'LEGAL', links: ['Privacy', 'Terms'] },
              ].map((section) => (
                <div key={section.title}>
                  <p className="text-white font-sans font-semibold text-xs mb-3 tracking-wider">{section.title}</p>
                  <ul className="space-y-2 text-white/60 text-sm">
                    {section.links.map((link) => (
                      <li key={link}>{link}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
